import { useEffect, useRef } from 'react';
import { doc, setDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getFcmRegistrationToken, onForegroundMessage } from '../../firebase/messaging';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

// Requests permission, registers FCM token, and stores it on the user doc.
export default function FcmBootstrap() {
  const { currentUser } = useRealtime();
  const syncingRef = useRef(false);

  useEffect(() => {
    if (!currentUser) return;

    const syncToken = async () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      try {
        // Ask for notification permission if not already granted/denied
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
          await Notification.requestPermission();
        }

        if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
          syncingRef.current = false;
          return;
        }

        const token = await getFcmRegistrationToken();
        if (!token) {
          syncingRef.current = false;
          return;
        }

        // Store token on user document
        await setDoc(
          doc(db, 'users', currentUser.uid),
          {
            fcmTokens: arrayUnion(token),
            fcmTokenUpdatedAt: serverTimestamp()
          },
          { merge: true }
        );

        // Handle foreground messages — show a toast so the user sees it immediately
        await onForegroundMessage((payload) => {
          const title = payload?.notification?.title || 'New Notification';
          const body = payload?.notification?.body || '';
          toast(`🔔 ${title}${body ? ': ' + body : ''}`, {
            duration: 6000,
            style: { maxWidth: 400 }
          });
        });
      } catch (err) {
        // Swallow errors to avoid breaking app load; logs aid debugging
        console.error('FCM setup failed', err);
      } finally {
        syncingRef.current = false;
      }
    };

    syncToken();
  }, [currentUser]);

  return null;
}
